import React from 'react'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import {
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
  MetricHighlight,
} from '../../../BuildingCharacteristics.styles'
import type { NeighborhoodComparisonCardProps } from '../ConstructionTimelineTab.types'

export const NeighborhoodComparisonCard: React.FC<
  NeighborhoodComparisonCardProps
> = ({
  buildingYear,
  buildingAge,
  isAgeEstimate,
  neighborsMeanYear,
  currentYear,
  t,
}) => {
  const neighborsMeanAge = currentYear - Math.round(neighborsMeanYear)

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Icon name="map" size="small" />
          {t('timeline.ageComparison.title')}
          <InfoTooltip>
            <TooltipMetricExplanation
              title={t('timeline.ageComparison.tooltip.title')}
              shortDescription={t('timeline.ageComparison.tooltip.description')}
              calculation={t('timeline.ageComparison.tooltip.calculation')}
              importance={t('timeline.ageComparison.tooltip.importance')}
              limitations={t('timeline.ageComparison.tooltip.limitations')}
              source={t('timeline.ageComparison.tooltip.source')}
            />
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <MetricGrid>
        <MetricRow>
          <MetricLabel>
            {t('timeline.ageComparison.labels.thisBuilding')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('timeline.ageComparison.tooltips.thisBuilding.title')}
                shortDescription={t(
                  'timeline.ageComparison.tooltips.thisBuilding.description'
                )}
                calculation={t(
                  'timeline.ageComparison.tooltips.thisBuilding.calculation'
                )}
                importance={t(
                  'timeline.ageComparison.tooltips.thisBuilding.importance'
                )}
                source={t(
                  'timeline.ageComparison.tooltips.thisBuilding.source'
                )}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {buildingYear} (
            {t('timeline.ageComparison.yearsOld', {
              count: buildingAge,
            })}
            )
            {isAgeEstimate && (
              <span style={{ fontSize: '12px', opacity: 0.7 }}>
                {' '}
                ({t('overview.labels.estimated')})
              </span>
            )}
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('timeline.ageComparison.labels.neighborhoodAverage')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t(
                  'timeline.ageComparison.tooltips.neighborhoodAverage.title'
                )}
                shortDescription={t(
                  'timeline.ageComparison.tooltips.neighborhoodAverage.description'
                )}
                calculation={t(
                  'timeline.ageComparison.tooltips.neighborhoodAverage.calculation'
                )}
                importance={t(
                  'timeline.ageComparison.tooltips.neighborhoodAverage.importance'
                )}
                source={t(
                  'timeline.ageComparison.tooltips.neighborhoodAverage.source'
                )}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricValue>
            {Math.round(neighborsMeanYear)} (
            {t('timeline.ageComparison.yearsOld', {
              count: neighborsMeanAge,
            })}
            )
          </MetricValue>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('timeline.ageComparison.labels.comparison')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('timeline.ageComparison.tooltips.comparison.title')}
                shortDescription={t(
                  'timeline.ageComparison.tooltips.comparison.description'
                )}
                categories={[
                  {
                    label: t(
                      'timeline.ageComparison.comparisonTypes.newerThanAverage'
                    ),
                    description: t(
                      'timeline.ageComparison.comparisonTypes.newerDescription'
                    ),
                  },
                  {
                    label: t(
                      'timeline.ageComparison.comparisonTypes.similarAge'
                    ),
                    description: t(
                      'timeline.ageComparison.comparisonTypes.similarDescription'
                    ),
                  },
                  {
                    label: t(
                      'timeline.ageComparison.comparisonTypes.significantlyOlder'
                    ),
                    description: t(
                      'timeline.ageComparison.comparisonTypes.olderDescription'
                    ),
                  },
                ]}
                importance={t(
                  'timeline.ageComparison.tooltips.comparison.importance'
                )}
                source={t('timeline.ageComparison.tooltips.comparison.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricHighlight
            $type={
              buildingYear > neighborsMeanYear
                ? 'success'
                : buildingYear < neighborsMeanYear - 20
                  ? 'warning'
                  : 'info'
            }
          >
            {buildingYear > neighborsMeanYear
              ? t('timeline.ageComparison.comparisonTypes.newerThanAverage')
              : buildingYear < neighborsMeanYear - 20
                ? t('timeline.ageComparison.comparisonTypes.significantlyOlder')
                : t('timeline.ageComparison.comparisonTypes.similarAge')}
          </MetricHighlight>
        </MetricRow>
      </MetricGrid>
    </Card>
  )
}
